import { Button, buttonVariants } from "../../components/ui/button";
import BackIcon from "../../components/back-icon";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { format } from "date-fns";
import { cn } from "../../lib/utils";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDoctor } from "../../lib/hooks/doctors/use-doctor";
import { Calendar, CircleUserRound, Clock, Hospital, Video } from 'lucide-react';
import { useAppointmentData } from "../../contexts/appointment-data-context";
import { useToast } from "../../components/ui/use-toast";
import { useDoctorSlots } from "../../lib/hooks/doctors/use-doctor-slots";
import { useEffect, useMemo, useState } from "react";
import DoctorDetails1 from "./large-doctor-details";
import { Helmet } from "react-helmet";
import UpperNav from "../../components/upper-nav";
import { AppointmentChip } from "../../components/appointment-chip";
import FaqTeleconsultation from "../../components/faq-teleconsultation";
import Loader from "../../components/ui/Loader";
import Spinner from "../../components/ui/spinner";

const MAX_SLOTS = 6;
const MAX_VISIBLE_CLINICS = 1;
const today = format(new Date(), "dd-MM-yyyy");

function getImageSizeFromBase64(base64: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = function () {
      resolve({ width: img.width, height: img.height });
    };
    img.onerror = reject;
    img.src = `${base64}`;
  });
}


function useIsLargeDevice() {
  const [isLargeDevice, setIsLargeDevice] = useState(window.innerWidth >= 1024);
  useEffect(() => {
    function handleResize() {
      setIsLargeDevice(window.innerWidth >= 1024);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return isLargeDevice;
}

function DoctorDetails() {
  const isLargeDevice = useIsLargeDevice();
  const [searchParams] = useSearchParams();
  const backUrl = searchParams.get("backUrl") || "/doctors";
  const [imageSize, setImageSize] = useState<{ width: number; height: number } | null>(null);
  const params = useParams();
  const professionalId = params.professionalId?.split("-").pop();
  const { toast } = useToast();

  const navigate = useNavigate();
  const { doctor, isLoading } = useDoctor(professionalId);
  useEffect(() => {
    const isLargeScreen = window.innerWidth >= 1024; // Same breakpoint used in useIsLargeDevice

    if (
      !isLoading &&
      doctor &&
      window.location.pathname === `/doctors/${professionalId}` &&
      !isLargeScreen
    ) {
      const name = doctor.fullName?.toLowerCase().replace(/\s+/g, "-") || "unknown";
      const specialty =
        doctor.specialities
          ?.map((s) => s.toLowerCase().replace(/\s+/g, "-"))
          .join("-") || "general";

      // Extract location details from first branch
      const branch = doctor.branchDetails?.[0] || {};
      const state = branch.state?.trim() ? branch.state.toLowerCase().replace(/\s+/g, "-") : null;
      const district = branch.district?.trim() ? branch.district.toLowerCase().replace(/\s+/g, "-") : null;
      const city = branch.subDistrictOrCity?.trim() ? branch.subDistrictOrCity.toLowerCase().replace(/\s+/g, "-") : null;

      // Build dynamic location array
      const locationParts = [];
      if (state) locationParts.push(state);
      if (district) locationParts.push(district);
      if (city) locationParts.push(city);

      // Final SEO path format: name-specialty-[available locations]-professionalId
      const seoPath = `/doctors/${name}-${specialty}${locationParts.length ? `-${locationParts.join("-")}` : ""}-${professionalId}`;

      // Preserve existing search params & add backUrl
      const updatedSearchParams = new URLSearchParams(searchParams);
      updatedSearchParams.set("backUrl", backUrl);

      //console.log("Redirecting to SEO path:", `${seoPath}?${updatedSearchParams.toString()}`);

      navigate(`${seoPath}?${updatedSearchParams.toString()}`, { replace: true });
    }
  }, [isLoading, doctor, professionalId, navigate, searchParams, backUrl]);


  const { data, setData } = useAppointmentData();
  const [selectedBranch, setSelectedBranch] = useState<number | undefined>();

  const selectedBranchInPC = doctor?.branchDetails?.find(
    branch => branch.branchId === selectedBranch
  );

  const currentAppointmentType = (selectedBranchInPC?.isTeleconsultationEnable) ? data?.appointmentType || "TELECONSULTATION" : data?.appointmentType || "WALK_IN";
  // console.log("currentAppointmentType", currentAppointmentType);


  useEffect(() => {
    if (!doctor) return;

    const type = selectedBranchInPC?.isTeleconsultationEnable ? "TELECONSULTATION" : "WALK_IN";

    setData({
      patient: data?.patient,
      appointmentType: type,
      slotTiming: data?.slotTiming,
      slotDate: data?.slotDate,
      dayOfWeek: data?.dayOfWeek,
      symptoms: data?.symptoms ?? [],
      healthRecords: data?.healthRecords,
      healthRecordPhysician: data?.healthRecordPhysician,
      branchDetails: data?.branchDetails ?? []
    });

    console.info("Auto selected appointment type:", type);
  }, [doctor]); // Only depend on 'doctor'

  const {
    slots = {},
    bookedSlotsData = {bookedSlots: [], patientPerSlot: 0 },
    isLoading: isSlotsLoading,
    isFetched: isSlotsFetched,
  } = useDoctorSlots(professionalId, selectedBranch);

  const [bookedSlots, setBookedSlots] = useState<any[]>([]);
  const [patientPerSlot, setPatientPerSlot] = useState<number>(0);

  useEffect(() => {
    if (bookedSlotsData) {
      setBookedSlots(bookedSlotsData.bookedSlots);
      setPatientPerSlot(bookedSlotsData.patientPerSlot);
    }
  }, [bookedSlotsData]);

  const isBooked = (slot: any) => {
    if (!bookedSlots || patientPerSlot === 0) return false;
    // Count the booked slots for the current slot
    const bookedCount = bookedSlots.filter(
      (bSlot) =>
        bSlot.from === slot.from &&
        bSlot.to === slot.to &&
        bSlot.date === selectedDate
    ).length;
    return bookedCount >= patientPerSlot;    
  };
  
  useEffect(() => {
    if (doctor?.profilePhoto) {
      getImageSizeFromBase64(doctor.profilePhoto)
        .then(setImageSize)
        .catch((err) => console.error("Failed to load image size", err));
    }
  }, [doctor?.profilePhoto]);


  function formatSlotText(slots: Record<string, { from: string }[]>) {
    const formatted = Object.entries(slots)
      .flatMap(([date, slotList]) =>
        slotList.map((slot) => {
          const [day, month, year] = date.split("-").map(Number);
          const [time, period] = slot.from.split(" ");
          const [hour, minute] = time.split(":").map(Number);
          const hour24 =
            period.toLowerCase() === "pm" && hour !== 12
              ? hour + 12
              : period.toLowerCase() === "am" && hour === 12
                ? 0
                : hour;

          const d = new Date(year, month - 1, day, hour24, minute);
          const formattedDate = d.toLocaleString("en-IN", {
            weekday: "short",
            day: "2-digit",
            month: "short",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          });

          return formattedDate.replace(",", "");
        })
      );

    return formatted.length ? ` Available slots: ${formatted.join(", ")}.` : "";
  }


  function getOpeningHoursFromSlots(slots: Record<string, { from: string; to: string }[]>) {
    const daySlotMap: Record<string, string[]> = {};

    Object.entries(slots).forEach(([date, timeRanges]) => {
      // Skip if timeRanges is not an array or empty
      if (!Array.isArray(timeRanges) || !timeRanges.length) return;

      const [day, month, year] = date.split("-").map(Number);
      // Validate date components
      if (isNaN(day) || isNaN(month) || isNaN(year)) return;

      const jsDate = new Date(year, month - 1, day);
      const dayOfWeek = jsDate.toLocaleDateString("en-US", { weekday: "long" });

      // Filter valid time ranges
      const times = timeRanges
        .filter((t) => typeof t.from === "string" && typeof t.to === "string")
        .map(({ from, to }) => `${from} - ${to}`);

      if (!daySlotMap[dayOfWeek]) daySlotMap[dayOfWeek] = [];
      daySlotMap[dayOfWeek].push(...times);
    });

    const finalSpec: { "@type": string; dayOfWeek: string; opens: string; closes: string }[] = [];

    for (const [day, timeStrings] of Object.entries(daySlotMap)) {
      const times = timeStrings
        .map((str) => {
          const [from] = str.split(" - ");
          if (!from) return null;

          const [time, period] = from.split(" ");
          if (!time || !period) return null;

          const [h, m] = time.split(":").map(Number);
          if (isNaN(h) || isNaN(m)) return null;

          let hour = period.toLowerCase() === "pm" && h !== 12 ? h + 12 : period.toLowerCase() === "am" && h === 12 ? 0 : h;
          return hour * 60 + m;
        })
        .filter((t): t is number => t !== null);

      if (!times.length) continue;

      const opensMin = Math.min(...times);
      const closesMin = Math.max(...times) + 15; // Add 15 mins buffer

      const pad = (n: number) => n.toString().padStart(2, "0");
      const opens = `${pad(Math.floor(opensMin / 60))}:${pad(opensMin % 60)}`;
      const closes = `${pad(Math.floor(closesMin / 60))}:${pad(closesMin % 60)}`;

      finalSpec.push({
        "@type": "OpeningHoursSpecification",
        dayOfWeek: day,
        opens,
        closes,
      });
    }

    return finalSpec;
  }

  const helmetContent = doctor && !isLoading ? (
    <>
      <Helmet>
        <title>
          {`Dr ${doctor.fullName || "Doctor"} | ${doctor.specialities?.join(", ") || "Doctor"} | Book Appointment`}
        </title>
        <meta
          name="description"
          content={
            (() => {
              const slotText = formatSlotText(slots);
              const awardsText =
                doctor.awardAndRecognition?.length && Array.isArray(doctor.awardAndRecognition)
                  ? ` Awards and Recognition: ${doctor.awardAndRecognition.filter((a) => typeof a === "string").join(", ")}.`
                  : "";

              if (doctor.aboutDoctor && doctor.aboutDoctor.trim() !== "") {
                return `${doctor.aboutDoctor}${awardsText}`;
              } else {
                return `Consult Dr ${doctor.fullName || "Doctor"}, a specialist in ${doctor.specialities?.join(", ") || "various medical fields"
                  }. Book appointments online at ${doctor.branchDetails?.[0]?.clinicName || "clinics"
                  }${awardsText} `;
              }
            })()
          }
        />
        <meta
          property="og:title"
          content={`Dr ${doctor.fullName || "Doctor"} | ${doctor.specialities?.join(", ") || "Doctor"} | Book Appointment`}
        />
        <meta
          property="og:description"
          content={
            (() => {
              const slotText = formatSlotText(slots);
              const awardsText =
                doctor.awardAndRecognition?.length && Array.isArray(doctor.awardAndRecognition)
                  ? ` Awards and Recognition: ${doctor.awardAndRecognition.filter((a) => typeof a === "string").join(", ")}.`
                  : "";

              if (doctor.aboutDoctor && doctor.aboutDoctor.trim() !== "") {
                return `${doctor.aboutDoctor}${awardsText}.${slotText}${doctor.fee && Number(doctor.fee) > 0 ? ` Fee: ₹${doctor.fee}.` : ""
                  }`;
              } else {
                return `Consult Dr ${doctor.fullName || "Doctor"}, a specialist in ${doctor.specialities?.join(", ") || "various medical fields"
                  }. Book appointments online at ${doctor.branchDetails?.[0]?.clinicName || "clinics"
                  }${awardsText}.${slotText}${doctor.fee && Number(doctor.fee) > 0 ? ` Fee: ₹${doctor.fee}.` : ""
                  }`;
              }
            })()
          }
        />
        <meta property="og:type" content="profile" />
        {doctor.profilePhoto && (
          <meta
            property="og:image"
            content={`${doctor.profilePhoto}`}
          />
        )}


        <meta property="og:url" content={window.location.href} />
        <link rel="canonical" href={window.location.href} />
        <script type="application/ld+json">
          {JSON.stringify(
            {
              "@context": "https://schema.org",
              "@type": "Physician",
              name: doctor.fullName ? `Dr ${doctor.fullName}` : "Doctor",
              ...(doctor.specialities?.length && {
                medicalSpecialty:
                  typeof doctor.specialities === "string"
                    ? doctor.specialities
                    : Array.isArray(doctor.specialities)
                      ? doctor.specialities.filter((s) => typeof s === "string").join(", ")
                      : "",
              }),
              ...(doctor.aboutDoctor &&
                typeof doctor.aboutDoctor === "string" &&
                doctor.aboutDoctor.trim() !== "" && {
                description: doctor.aboutDoctor,
              }),
              ...(doctor.branchDetails?.[0] && {
                address: {
                  "@type": "PostalAddress",
                  ...(doctor.branchDetails[0].addressLine1 &&
                    typeof doctor.branchDetails[0].addressLine1 === "string" && {
                    streetAddress: doctor.branchDetails[0].addressLine1,
                  }),
                  ...(doctor.subDistrictOrCity &&
                    typeof doctor.subDistrictOrCity === "string" && {
                    addressLocality: doctor.subDistrictOrCity,
                  }),
                  ...(doctor.branchDetails[0].state &&
                    typeof doctor.branchDetails[0].state === "string" && {
                    addressRegion: doctor.branchDetails[0].state,
                  }),
                  ...(doctor.branchDetails[0].pincode &&
                    typeof doctor.branchDetails[0].pincode === "string" && {
                    postalCode: doctor.branchDetails[0].pincode,
                  }),
                  addressCountry: "IN",
                },
              }),
              ...(doctor.fee && Number(doctor.fee) > 0 && {
                priceRange: `₹${doctor.fee}`,
              }),
              ...(doctor.nameOfDegreeOrDiplomaObtained?.length && {
                alumniOf:
                  Array.isArray(doctor.nameOfDegreeOrDiplomaObtained)
                    ? doctor.nameOfDegreeOrDiplomaObtained
                      .filter((d) => typeof d === "string")
                      .join(", ")
                    : "",
              }),
              ...(doctor.awardAndRecognition?.length && {
                award:
                  Array.isArray(doctor.awardAndRecognition)
                    ? doctor.awardAndRecognition.filter((a) => typeof a === "string")
                    : [],
              }),
              ...(doctor.profilePhoto &&
                imageSize &&
                typeof doctor.profilePhoto === "string" && {
                image: {
                  "@type": "ImageObject",
                  contentUrl: `${doctor.profilePhoto}`,
                  width: Number(imageSize.width) || 0,
                  height: Number(imageSize.height) || 0,
                },
              }),
              ...(slots &&
                Object.keys(slots).some((date) => Array.isArray(slots[date]) && slots[date].length > 0) && {
                openingHoursSpecification: getOpeningHoursFromSlots(slots),
              }),
              url: window.location.href,
            },
            (key, value) => {
              // Replace Symbols and non-serializable values with null
              if (typeof value === "symbol") return null;
              if (value instanceof Symbol) return null;
              return value;
            }
          )}
        </script>
      </Helmet>
    </>
  ) : null;
  // (
  //   <Helmet>
  //     <title>Loading Doctor Details...</title>
  //     <meta name="description" content="Loading doctor details..." />
  //   </Helmet>
  // );
  const [selectedDate, setSelectedDate] = useState(today);
  const selectedDateSlots = useMemo(
    () => slots[selectedDate] ?? [],
    [slots, selectedDate]
  );

  const [paginatedSlots, setPaginatedSlots] = useState<
    typeof selectedDateSlots
  >([]);

  function handleSelectSlot(slot: { from: string; to: string }) {
    const [day, month, year] = selectedDate.split("-").map(Number);
    const jsDate = new Date(year, month - 1, day);

    setData({
      ...data,
      slotTiming: `${slot.from} - ${slot.to}`,
      dayOfWeek: format(jsDate, "EEEE"),
      slotDate: jsDate,
      branchDetails: data?.branchDetails || [],
    });
  }

  function handleContinue() {
    console.log("currentAppointmentType: ", currentAppointmentType);
    setData({
      ...data,
      appointmentType: currentAppointmentType,
      branchDetails: data?.branchDetails ?? []
    })
    if (data?.slotTiming && data?.slotDate && data?.dayOfWeek)
      navigate(`/doctors/${professionalId}/appointment`);
    else
      toast({
        title: "Select a slot",
        description: "Please select a slot to continue",
        variant: "destructive",
      });
  }

  useEffect(() => {
    if (isSlotsFetched) {
      setSelectedDate(Object.keys(slots)[0]);
    }
  }, [isSlotsFetched, slots]);

  // useEffect(() => {
  //   if (selectedDateSlots.length) {
  //     setPaginatedSlots(selectedDateSlots.slice(0, MAX_SLOTS));
  //   }
  // }, [selectedDateSlots]);

  useEffect(() => {
    if (selectedDateSlots.length) {
      const now = new Date();

      // Filter out past slots first
      const activeSlots = selectedDateSlots.filter((slot) => {
        const [day, month, year] = selectedDate.split("-").map(Number);
        const [time, period] = slot.from.split(" ");
        const [startHour, startMinute] = time.split(":").map(Number);

        const hour24 =
          period.toLowerCase() === "pm" && startHour !== 12
            ? startHour + 12
            : period.toLowerCase() === "am" && startHour === 12
              ? 0
              : startHour;

        const slotStartTime = new Date(
          year,
          month - 1,
          day,
          hour24,
          startMinute
        );

        // Return true only for future slots (not passed)
        return slotStartTime >= now;
      });

      // Then apply pagination to active slots only
      setPaginatedSlots(activeSlots.slice(0, MAX_SLOTS));
    }
  }, [selectedDateSlots, selectedDate]);

  const branch = doctor?.branchDetails?.[0];
  // console.log({ branch });
  // console.log(doctor);

  const [showAllClinics, setShowAllClinics] = useState(false);

  const branchDetails = doctor?.branchDetails || [];
  const visibleBranches = showAllClinics
    ? branchDetails
    : branchDetails.slice(0, MAX_VISIBLE_CLINICS);

  useEffect(() => {
    if (doctor?.branchDetails?.length) {
      const defaultBranch = doctor.branchDetails[0];
      setSelectedBranch(defaultBranch.branchId);
      setData({
        ...data,
        branchDetails: [defaultBranch],
      });
    }
  }, [doctor, setData]);

  const handleBranchSelect = (branchId: number) => {
    const selectedBranch1 = doctor?.branchDetails?.find(
      (branch) => branch.branchId === branchId
    );

    setSelectedBranch(branchId);

    if (selectedBranch1) {
      setData({
        ...data,
        branchDetails: [selectedBranch1],
      });
    }
  };

  if (isLargeDevice) {
    return (
      <>
        {helmetContent}
        <DoctorDetails1 />
      </>
    );
  }

  const handleAppointmentClick = (e: React.MouseEvent, type: "WALK_IN" | "TELECONSULTATION") => {
    e.preventDefault();
    e.stopPropagation();

    // Use current data instead of undefined 'prev'
    setData({
      patient: data?.patient,
      appointmentType: type,
      slotTiming: data?.slotTiming,
      slotDate: data?.slotDate,
      dayOfWeek: data?.dayOfWeek,
      symptoms: data?.symptoms ?? [],
      healthRecords: data?.healthRecords,
      healthRecordPhysician: data?.healthRecordPhysician,
      branchDetails: data?.branchDetails ?? []
    });
  };

  if (isLoading || !doctor) {
    return (
      <>
        <UpperNav />
        <div className="flex items-center justify-center h-[80vh]">
          <Spinner />
          {/* <Loader /> */}
        </div>
      </>
    );
  }

  return (
    <>
      {helmetContent}

      {/* <UpperNav /> */}
      <div className="py-7 mt-0 sm:mt-0">
        <div className="container relative">
          {/* Only hide back button when directly navigating to the URL (no referrer) */}
          {!isLargeDevice && !window.location.pathname.includes(doctor?.fullName?.toLowerCase().replace(/\s+/g, "-") || "") && (
            <Link
              to={backUrl}
              className={buttonVariants({
                className: "absolute left-2 sm:top-0",
                variant: "ghost",
                size: "icon",
              })}
              onClick={() => setData({ branchDetails: [] })}
            >
              <BackIcon />
              <span className="absolute top-full left-1/2 transform -translate-x-1/2 mt-1 hidden group-hover:block items-center px-2 py-1 rounded text-xs text-white bg-gray-800 shadow-lg z-50">
                Go Back
              </span>
            </Link>
          )}

          <div className="space-y-6">
            {doctor?.profilePhoto ? (
              <img
                src={`${doctor.profilePhoto}`}
                alt={doctor.fullName ?? "Doctor"}
                className="mx-auto size-28 rounded-full object-contain"
              />
            ) : (
              <div className="mx-auto size-28 rounded-full object-contain">
                <CircleUserRound
                  className="size-full"
                  strokeWidth={1}
                />
              </div>
            )}
            <div className="text-center">
              <h2 className="text-lg font-bold">Dr {doctor?.fullName ?? "N/A"}</h2>
              {doctor?.specialities && doctor.specialities.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  {doctor.specialities.join(", ")}
                </p>
              )}
              <p className="mt-2 text-sm">
                {doctor?.nameOfDegreeOrDiplomaObtained && doctor.nameOfDegreeOrDiplomaObtained.length > 0
                  ? doctor.nameOfDegreeOrDiplomaObtained.join(", ")
                  : ""}
              </p>
            </div>
          </div>
        </div>
        <div className="my-5 h-1.5 bg-border" />
        <div className="container space-y-6">
          <div className="mb-6 space-y-4">
            <h2 className="mb-4 font-bold text sm:text-left">Appointment Type</h2>

            {/* Chip container with center alignment */}
            <div className="flex items-center justify-center gap-3 flex-wrap max-sm:gap-2">
              {selectedBranchInPC?.isInClinicConsultationEnable && <AppointmentChip
                type="WALK_IN"
                icon={<Hospital className="w-4 h-4 max-sm:w-3 max-sm:h-3" />}
                label="In-clinic Appointment"
                shortLabel="In-clinic Appointment"
                isSelected={currentAppointmentType === "WALK_IN"}
                onClick={handleAppointmentClick}
              />}

              {selectedBranchInPC?.isTeleconsultationEnable && <AppointmentChip
                type="TELECONSULTATION"
                icon={<Video className="w-4 h-4 max-sm:w-3 max-sm:h-3" />}
                label="Teleconsultation"
                shortLabel="Teleconsultation"
                isSelected={currentAppointmentType === "TELECONSULTATION"}
                onClick={handleAppointmentClick}
              />}

              {!(selectedBranchInPC?.isTeleconsultationEnable) && !(selectedBranchInPC?.isInClinicConsultationEnable) && (
                <div className="flex items-center justify-center">
                  <p>Not Available Now</p>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="my-5 h-1.5 bg-border" />
        <div className="container space-y-6">
          <div className="mb-6 space-y-4">
            {visibleBranches.map((branch) => (
              <div key={branch.branchId}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="clinicBranch"
                        checked={selectedBranch === branch.branchId}
                        onChange={() => handleBranchSelect(branch.branchId)}
                        className="mr-2"
                        style={{
                          accentColor: "hsl(var(--primary-2))",
                        }}
                      />
                      <h4 className="text-sm font-bold">{branch.clinicName}</h4>
                    </label>
                    {
                      // Only render if at least one field is non-null
                      (branch?.addressLine1 || branch?.addressLine2 || branch?.state || branch?.pincode) && (
                        <>
                          {
                            // Render first <p> only if addressLine1 or addressLine2 is non-null
                            (branch?.addressLine1 || branch?.addressLine2) && (
                              <p className="text-sm ml-6">
                                {[
                                  branch?.addressLine1,
                                  branch?.addressLine2
                                ].filter(Boolean).join(", ")}
                              </p>
                            )
                          }
                          {
                            // Render second <p> only if state or pincode is non-null
                            (branch?.state || branch?.pincode || branch?.subDistrictOrCity || branch?.district) && (
                              <p className="text-sm ml-6">
                                {[
                                  branch?.state,
                                  branch?.district,
                                  branch?.subDistrictOrCity,
                                  branch?.pincode
                                ].filter(Boolean).join(", ")}
                              </p>
                            )
                          }
                        </>
                      )
                    }
                  </div>
                  {branch?.fee != null && Number(branch.fee) !== 0 && (
                    <p className="text-sm font-bold">
                      Fee : {branch?.fee == null || Number(branch?.fee) === 0.0 ? "NA" : `₹${branch.fee}`}
                    </p>)}            </div>
              </div>
            ))}
            {branchDetails.length > MAX_VISIBLE_CLINICS && !showAllClinics && (
              <Button
                variant="link"
                className="!mt-0 p-0 text-xs font-bold text-primary underline"
                onClick={() => setShowAllClinics(true)}
              >
                See more clinics
              </Button>
            )}
          </div>
          {isSlotsLoading ? (
            <p>Loading...</p>
          ) : (
            <div>
              <h2 className="mb-4 font-bold">Select a date and time</h2>
              <Tabs
                value={selectedDate}
                onValueChange={(val) => {
                  setSelectedDate(val);
                  setData({
                    ...data,
                    branchDetails: data?.branchDetails ?? [],
                    slotTiming: "",
                    dayOfWeek: "",
                    slotDate: new Date(),
                  });
                }}
              >
                <TabsList className="mb-4 h-[unset] w-full justify-normal overflow-x-auto rounded-none border-b-2 p-0">
                  {Object.keys(slots).map((slot) => {
                    const today = format(new Date(), "dd-MM-yyyy");

                    const [day, month, year] = slot.split("-").map(Number);
                    const jsDate = new Date(year, month - 1, day);

                    const isToday = slot === today;
                    const dateStr = isToday ? "Today" : format(jsDate, "dd MMM");

                    return (
                      <TabsTrigger
                        key={slot}
                        value={slot}
                        className="rounded-t-md !border-0 text-sm font-bold text-foreground data-[state=active]:bg-primary-2 data-[state=active]:text-primary-foreground"
                      >
                        {dateStr}
                      </TabsTrigger>
                    );
                  })}
                </TabsList>

                <TabsContent
                  value={selectedDate}
                  className="flex flex-col justify-center"
                >
                  {(() => {

                    // Check if teleconsultation and in-clinic consultation are enabled
                    if (!(selectedBranchInPC?.isTeleconsultationEnable) && !(selectedBranchInPC?.isInClinicConsultationEnable)) {

                      return (
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                            <Calendar className="w-8 h-8 text-gray-400" />
                          </div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">
                            No appointments available
                          </h3>
                          {/* <p className="text-sm text-gray-500 max-w-sm">
                            {isToday
                              ? "No appointment slots are available for today."
                              : isTomorrow
                                ? "No appointment slots are available for tomorrow."
                                : "No appointment slots are available for this date."
                            }
                            <br />
                            Please select another date to view available time slots.
                          </p> */}
                        </div>
                      );
                    }

                    const now = new Date();
                    const today = format(new Date(), "dd-MM-yyyy");
                    const tomorrow = format(new Date(Date.now() + 24 * 60 * 60 * 1000), "dd-MM-yyyy");

                    // Filter active slots (future slots only)
                    const activeSlots = selectedDateSlots.filter((slot) => {
                      const [day, month, year] = selectedDate.split("-").map(Number);
                      const [time, period] = slot.from.split(" ");
                      const [startHour, startMinute] = time.split(":").map(Number);

                      const hour24 =
                        period.toLowerCase() === "pm" && startHour !== 12
                          ? startHour + 12
                          : period.toLowerCase() === "am" && startHour === 12
                            ? 0
                            : startHour;

                      const slotStartTime = new Date(
                        year,
                        month - 1,
                        day,
                        hour24,
                        startMinute
                      );

                      return slotStartTime >= now;
                    });

                    // If no slots exist for this date at all
                    if (selectedDateSlots.length === 0) {
                      const isToday = selectedDate === today;
                      const isTomorrow = selectedDate === tomorrow;

                      return (
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                            <Calendar className="w-8 h-8 text-gray-400" />
                          </div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">
                            No appointments available
                          </h3>
                          <p className="text-sm text-gray-500 max-w-sm">
                            {isToday
                              ? "No appointment slots are available for today."
                              : isTomorrow
                                ? "No appointment slots are available for tomorrow."
                                : "No appointment slots are available for this date."
                            }
                            <br />
                            Please select another date to view available time slots.
                          </p>
                        </div>
                      );
                    }

                    // If slots existed but all are in the past (no active slots remaining)
                    if (activeSlots.length === 0) {
                      const isToday = selectedDate === today;
                      const isTomorrow = selectedDate === tomorrow;

                      return (
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                          <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                            <Clock className="w-8 h-8 text-orange-500" />
                          </div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">
                            {isToday
                              ? "No slots remaining for today"
                              : isTomorrow
                                ? "No slots remaining for tomorrow"
                                : "No slots remaining for this date"
                            }
                          </h3>
                          <p className="text-sm text-gray-500 max-w-sm">
                            {isToday
                              ? "All appointment slots for today have passed or are no longer available."
                              : isTomorrow
                                ? "All appointment slots for tomorrow are no longer available."
                                : "All appointment slots for this date are no longer available."
                            }
                            <br />
                            Please choose another date to find available slots.
                          </p>
                        </div>
                      );
                    }

                    // If we have active slots, show them
                    return (
                      <>
                        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
                          {paginatedSlots.map(slot => {
                            const isSelected = data?.slotTiming === `${slot.from} - ${slot.to}`;
                            return (
                              <Button
                                key={slot.from}
                                variant="outline"
                                size="sm"
                                className={cn(
                                  "rounded-full border-2 text-xs lowercase",
                                  isSelected && "border-primary-2 !bg-primary-2 font-bold !text-primary-foreground"
                                )}
                                onClick={() => handleSelectSlot(slot)}
                                disabled={isBooked(slot)}
                              >
                                {slot.from} - {slot.to}
                              </Button>
                            );
                          })}
                        </div>

                        {/* Show "See all slots" button if there are more active slots */}
                        {activeSlots.length > MAX_SLOTS && paginatedSlots.length <= MAX_SLOTS && (
                          <Button
                            className="mt-7 text-xs font-bold text-primary underline"
                            variant="link"
                            onClick={() => setPaginatedSlots([...activeSlots])}
                          >
                            See all slots ({activeSlots.length} available)
                          </Button>
                        )}
                      </>
                    );
                  })()}
                </TabsContent>

              </Tabs>
            </div>
          )}
        </div>
        <div className="my-5 h-1.5 bg-border" />
        <div className="container space-y-6 pb-24">
          {doctor?.aboutDoctor && doctor.aboutDoctor.trim() !== "" && (
            <div>
              <h2 className="mb-2 text-xl font-bold">About Doctor</h2>
              <p className="text-sm sm:text-base sm:leading-7">
                <span>
                  {doctor?.aboutDoctor == null || doctor.aboutDoctor.trim() === "" ? (
                    <li className="text-sm">
                      NA
                    </li>
                  ) : (
                    doctor.aboutDoctor.split('\n').map((line, index) => (
                      <span key={index}>
                        {line}
                        <br />
                      </span>
                    ))
                  )}
                </span>
              </p>
            </div>
          )}
          {/* <div>
            <h2 className="mb-2 font-bold">Services</h2>
            <ul className="list-disc space-y-2 pl-4">
              {doctor?.services?.length ? (
                doctor?.services?.map((service) => (
                  <li key={service} className="text-sm">
                    {service}
                  </li>
                ))
              ) : (
                <li className="text-sm">No services listed</li>
              )}
            </ul>
          </div> */}
          <div>
            <h2 className="mb-2 font-bold">Specialisations</h2>
            <ul className="list-disc space-y-2 pl-4">
              {doctor?.specialities?.length ? (
                doctor?.specialities?.map((speciality) => (
                  <li key={speciality} className="text-sm">
                    {speciality}
                  </li>
                ))
              ) : (
                <li className="text-sm">No specialities listed</li>
              )}
            </ul>
          </div>

          {Number(doctor?.yearOfExperience) > 0 && (
            <div>
              <h2 className="mb-2 font-bold">Experience</h2>
              <ul className="list-disc space-y-2 pl-4">
                <li key={doctor?.yearOfExperience} className="text-sm">
                  {doctor?.yearOfExperience} years
                </li>
              </ul>
            </div>
          )}


          {doctor && Array.isArray(doctor?.nameOfDegreeOrDiplomaObtained) && doctor.nameOfDegreeOrDiplomaObtained.length > 0 && (
            <div>
              <h2 className="mb-2 font-bold">Education</h2>
              <ul className="list-disc space-y-2 pl-4">
                {doctor?.nameOfDegreeOrDiplomaObtained && doctor.nameOfDegreeOrDiplomaObtained.length > 0 ? (
                  doctor.nameOfDegreeOrDiplomaObtained.map((degree) => (
                    <li key={degree} className="text-sm">
                      {degree}
                    </li>
                  ))
                ) : (
                  <li className="text-sm">NA</li>
                )}
              </ul>
            </div>
          )}
          {doctor && Array.isArray(doctor?.awardAndRecognition) && doctor.awardAndRecognition.length > 0 && (
            <div>
              <h2 className="mb-2 font-bold">Rewards and Recognition </h2>
              <ul className="list-disc space-y-2 pl-4">
                {doctor?.awardAndRecognition?.length ? (
                  doctor?.awardAndRecognition.map((awd) => (
                    <li key={awd} className="text-sm">
                      {awd}
                    </li>
                  ))
                ) : (
                  <li className="text-sm">No rewards and recognition listed</li>
                )}
              </ul>
            </div>
          )}

          {selectedBranchInPC?.isTeleconsultationEnable && <div>
            <FaqTeleconsultation />
          </div>}

          <div className="fixed bottom-0 left-0 right-0 z-50 flex justify-center bg-white p-4 border-t border-gray-200 shadow-lg">
            <Button
              className="w-full max-w-sm"
              onClick={handleContinue}
              disabled={!(selectedBranchInPC?.isTeleconsultationEnable) && !(selectedBranchInPC?.isInClinicConsultationEnable)}
            >
              Continue
            </Button>
          </div>
        </div>
      </div >
    </>
  );
}

export default DoctorDetails;